<template>
  <div>
    <PageTitle
      :title="machine.name"
      color="bg-los-pink-base"
      pattern="locksteek"
      repeat="4"
    />
    <LayoutBasic>
      <div class="grid md:grid-cols-5 gap-x-16 gap-y-8">
        <MachineCarousel
          class="col-span-full md:col-span-3 lg:col-span-3"
          :pictures="machinePictures"
        />
        <div class="col-span-full md:col-span-2 lg:col-span-2">
          <h3 class="mb-4 text-xl font-semibold">{{ machine.name }}</h3>
          <div class="sm:mb-4">
            <span
              v-if="machine.old_price"
              class="hidden sm:inline-block font-normal"
              >Van
              <span>€{{ getPriceToFormat(machine.old_price) }}</span>
              voor</span
            >
            <span
              class="text-2xl sm:text-4xl whitespace-no-wrap ml-2 text-los-blue-darkest font-medium"
              >€{{ getPriceToFormat(machine.price) }}</span
            >
          </div>
          <div class="mt-4 md:mt-8" v-if="machine.brochure">
            <a class="btn-blue" :href="machine.brochure.url" target="_blank">
              <FontAwesome icon="file" class="text-lg mr-2" />Bekijk de brochure
            </a>
          </div>
          <div class="mt-4 md:mt-8">
            <div class="text-los-pink-darkest font-bold sm:text-lg mb-2">
              Maak direct een afspraak!
            </div>
            <div class="flex gap-x-3 w-full">
              <a
                href="https://wa.me/31713031232"
                target="_blank"
                class="bg-los-pink-darkest h-10 w-10 shadow rounded-full flex items-center justify-center pl-[.1rem]"
              >
                <span class="sr-only">Stuur ons een bericht op Whatsapp</span>
                <FontAwesome class="text-white h-[1.5rem]" :icon="faWhatsapp" />
              </a>
              <a
                href="tel:(071)3031232"
                target="_blank"
                class="bg-los-pink-darkest h-10 w-10 shadow rounded-full flex items-center justify-center"
              >
                <span class="sr-only">Bel ons</span>
                <FontAwesome class="text-white h-[1.1rem]" icon="phone" />
              </a>
              <a
                href="mailto:info@losnaaimachines.nl"
                target="_blank"
                class="bg-los-pink-darkest h-10 w-10 shadow rounded-full flex items-center justify-center"
              >
                <span class="sr-only">Mail ons</span>
                <FontAwesome class="text-white h-[1.1rem]" icon="envelope" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </LayoutBasic>

    <LayoutBasic nested-class="max-w-screen-lg">
      <div>
        <div class="mb-10">
          <h2 class="heading-basic font-platform">Over {{ machine.name }}</h2>
          <p class="text-basic">{{ machine.description }}</p>
        </div>
      </div>
    </LayoutBasic>

    <CtaRecommend :machines="recommendedMachines" />
    <LayoutBasic nested-class="max-w-screen-lg">
      <CtaMachineBrand :brand="machine.brand" :type="machine.type" />
    </LayoutBasic>
  </div>
</template>

<script setup>
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const { params } = useRoute();
const { findOne, find } = useStrapi();

const { data: machineData } = await useAsyncData('machine', () =>
  findOne('machines', params.slug, {
    populate: [
      'type',
      'brand',
      'brand.image',
      'list_image',
      'images',
      'brochure',
      'seo',
      'seo.metaImage',
    ],
  })
);

const { data: recommendedMachinesData } = await useAsyncData(
  'recommended-machines',
  () =>
    find('machines', {
      filters: {
        brand: { key: { $eq: params.brand } },
        slug: { $ne: params.slug },
      },
      populate: ['type', 'brand', 'list_image'],
      pagination: {
        pageSize: 8,
      },
    })
);

const machine = computed(() => {
  return machineData.value?.data.results[0];
});

const machinePictures = computed(() => {
  return [machine.value?.list_image ?? {}, ...(machine.value?.images ?? [])];
});

const recommendedMachines = computed(() => {
  return recommendedMachinesData.value?.data;
});

const getPriceToFormat = (money) => {
  return `${money.toLocaleString('nl')},-`;
};

usePageSeo({
  metaTitle:
    machine.value.seo?.metaTitle ?? machine.value.name + ' - Los Naaimachines',
  metaDescription:
    machine.value.seo?.metaDescription ??
    'Koop de ' +
      machine.value.name +
      ' bij Los Naaimachines in Roelofarendsveen. Kom langs in de winkel voor een demonstratie.',
  metaImage: machine.value.seo?.metaImage ?? machine.value.list_image,
  isArticle: true,
});

useHead({
  link: machinePictures.value?.[0]
    ? {
        href: useStrapiImage(machinePictures.value[0], 'large'),
        rel: 'preload',
        as: 'image',
      }
    : null,
});
</script>

<style lang="scss" scoped></style>
